import React from 'react';

import { useMachine } from '@xstate/react';

import type { JSONDocNode } from '@atlaskit/editor-json-transformer/types';

import type { EditorPluginAIConfigItem } from '../prebuilt/config-items/config-items';
import type {
	CommandPaletteContext,
	CommandPaletteEvent,
	CommandPaletteState,
} from '../utils/xstate/command-palette-state-machine';
import { commandPaletteStateMachine } from '../utils/xstate/command-palette-state-machine';

type UseCommandPaletteStateMachineOptions = {
	/**
	 * default AI generation config
	 */
	baseGenerate?: EditorPluginAIConfigItem;
	/**
	 * initial preset/ command to prefill the command palette with
	 */
	startingConfig: EditorPluginAIConfigItem;
	/**
	 * initial preset prompt label
	 */
	startingPresetPromptLabel?: string;
	startingUserADFInput?: JSONDocNode;

	/**
	 * userInput to prefill the command palette with
	 */
	startingUserInput?: string;
};

export function useCommandPaletteStateMachine({
	startingConfig,
	startingPresetPromptLabel,
	baseGenerate,
	startingUserInput,
	startingUserADFInput,
}: UseCommandPaletteStateMachineOptions): {
	/**
	 * current context or data stored in the command palette state machine
	 */
	context: CommandPaletteContext;
	/**
	 * function to send events to the command palette state machine
	 */
	send: (event: CommandPaletteEvent) => void;
	/**
	 * current state of the command palette state machine
	 */
	state: CommandPaletteState;
} {
	const machineRef = React.useRef<ReturnType<typeof commandPaletteStateMachine> | null>(null);

	if (machineRef.current === null) {
		machineRef.current = commandPaletteStateMachine({
			context: {
				textInput: startingUserInput,
				adfInput: startingUserADFInput,
				configItem: startingConfig,
				presetPromptLabel: startingPresetPromptLabel,
				baseGenerate,
			},
		});
	}

	// Ignored via go/ees005
	// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
	const [state, send] = useMachine(machineRef.current!);

	React.useEffect(() => {
		if (baseGenerate) {
			send({
				type: 'updateBaseGenerateAndConfigItem',
				baseGenerate,
				configItem: startingConfig,
			});
		}
	}, [baseGenerate, startingConfig, send]);

	return {
		// In a later version of xstate -- the type gen means the machine
		// has good types, so these assertions are not required
		state: state.value as CommandPaletteState,
		context: state.context as CommandPaletteContext,
		// Out of the box -- the type of the event received by the xstate send
		// will allow any "payload".
		// ie. `send("correct event type", {incorrect: "event payload"})`
		//
		// To avoid related issues -- we've limited the type to only allow
		// sending the full event.
		// ie. `send({type: "correct event type", correct: "event payload"})`
		send: send as (event: CommandPaletteEvent) => void,
	};
}
